import * as React from 'react';
import cytoscape, {Core, ElementDefinition, Position} from 'cytoscape';
import {GraphLayout, GraphNode, GraphEdge} from '../interface';
import {getConstantStyles} from './get-constant-styles';
import {isSelectMultipleMouseEvent} from '../../../utils/get-client-os';
import {createEdgeElement, createNodeElement} from './create-elements';
import {useHandleBoxSelection} from './use-handle-box-selection';
import {defaultZoom} from '../../../application/query-workflow/query-layout/completed-query/results-graph/use-camera-props';
import {DEFAULT_PADDING} from '../constants';

interface Props {
    nodes: GraphNode[];
    edges: GraphEdge[];
    csRef: React.MutableRefObject<Core | null>;
    nodePositions: GraphLayout;
    handleNodeMoved: (value: GraphLayout) => void;
    zoom: number;
    setZoom: (val: number) => void;
    pan: Position;
    setPan: (val: Position) => void;
    setHoveredNodes: (nodeIds: number[]) => void;
    selectNodes: (nodeId: number[], isSelectMultiple: boolean) => void;
    changesClearedOrSavedCounter?: number;
    getIsNodePartiallyHidden: (nodeId: number) => boolean;
    getIsNodeSelected: (nodeId: number) => boolean;
    getIsEdgeSelected: (edgeId: number) => boolean;
    getIsNodeFullyHidden: (nodeId: number) => boolean;
    getIsEdgePartiallyHidden: (edgeId: number) => boolean;
    getIsEdgeFullyHidden: (edgeId: number) => boolean;
    isHiddenNodesVisible: boolean;
}

const defaultMinZoom = 0.1;
export const graphElementId = 'cytoscape-graph';

export function useCytoscapeInitialization(props: Props) {
    const {
        csRef,
        nodes,
        edges,
        nodePositions,
        handleNodeMoved,
        zoom,
        setZoom,
        pan,
        setPan,
        setHoveredNodes,
        selectNodes,
        changesClearedOrSavedCounter,
        getIsNodePartiallyHidden,
        getIsNodeFullyHidden,
        getIsEdgePartiallyHidden,
        getIsEdgeFullyHidden,
        getIsNodeSelected,
        getIsEdgeSelected
    } = props;

    const handleBoxSelection = useHandleBoxSelection(selectNodes);

    const zoomRef = React.useRef<number>(0);
    const panRef = React.useRef({x: 0, y: 0});
    const selectNodesRef = React.useRef(selectNodes);
    const getIsNodePartiallyHiddenRef = React.useRef(getIsNodePartiallyHidden);
    const getIsNodeFullyHiddenRef = React.useRef(getIsNodeFullyHidden);
    const getIsEdgePartiallyHiddenRef = React.useRef(getIsEdgePartiallyHidden);
    const getIsEdgeFullyHiddenRef = React.useRef(getIsEdgeFullyHidden);
    const handleBoxSelectionRef = React.useRef(handleBoxSelection);
    const handleNodeMovedRef = React.useRef(handleNodeMoved);
    const getIsNodeSelectedRef = React.useRef(getIsNodeSelected);
    const getIsEdgeSelectedRef = React.useRef(getIsEdgeSelected);

    zoomRef.current = zoom;
    panRef.current = pan;
    selectNodesRef.current = selectNodes;
    getIsNodePartiallyHiddenRef.current = getIsNodePartiallyHidden;
    getIsNodeFullyHiddenRef.current = getIsNodeFullyHidden;
    getIsEdgePartiallyHiddenRef.current = getIsEdgePartiallyHidden;
    getIsEdgeFullyHiddenRef.current = getIsEdgeFullyHidden;
    handleBoxSelectionRef.current = handleBoxSelection;
    handleNodeMovedRef.current = handleNodeMoved;
    getIsNodeSelectedRef.current = getIsNodeSelected;
    getIsEdgeSelectedRef.current = getIsEdgeSelected;

    React.useEffect(() => {
        csRef.current = cytoscape({
            container: document.getElementById(graphElementId),
            elements: {
                nodes: nodes
                    .filter((node) => !getIsNodeFullyHiddenRef.current(node.id))
                    .map((node) =>
                        createNodeElement(
                            node,
                            nodePositions,
                            getIsNodePartiallyHiddenRef.current,
                            getIsNodeSelectedRef.current(node.id)
                        )
                    ),
                edges: edges
                    .filter((edge) => !getIsEdgeFullyHiddenRef.current(edge.id))
                    .map((edge) =>
                        createEdgeElement(
                            edge,
                            getIsEdgePartiallyHiddenRef.current,
                            getIsEdgeSelectedRef.current(edge.id)
                        )
                    )
            },
            style: [...getConstantStyles()],
            layout: {
                name: 'preset'
            }
        });

        csRef.current?.on('grab', () => {
            setHoveredNodes([]);
        });

        csRef.current?.on('dragfree', () => {
            const result: GraphLayout = {};

            // @ts-ignore
            const elements = csRef.current.json().elements.nodes;
            elements!.forEach((node: ElementDefinition) => {
                result[node.data.id!] = node.position!;
            });

            handleNodeMovedRef.current(result);
        });

        csRef.current?.on('zoom', () => {
            setZoom(csRef.current!.zoom());
        });
        csRef.current?.on('pan', () => {
            setPan(csRef.current!.pan());
        });

        csRef.current?.fit(undefined, DEFAULT_PADDING);

        csRef.current?.maxZoom(1);
        csRef.current?.minZoom(Math.min(csRef.current?.zoom(), defaultMinZoom));

        if (zoomRef.current != Number(defaultZoom)) {
            csRef.current?.zoom(zoomRef.current);
        }

        if (panRef.current.x || panRef.current.y) {
            csRef.current?.pan(panRef.current);
        }

        csRef.current?.on('mouseover', 'node', (e) => {
            const node = e.target;

            const {id} = node.data();

            setHoveredNodes([id]);
        });

        csRef.current?.on('mouseout', 'node', () => {
            setHoveredNodes([]);
        });

        csRef.current?.on('click', 'node', (e) => {
            const isSelectMultiple = isSelectMultipleMouseEvent(e.originalEvent);

            selectNodesRef.current([Number(e.target._private.data.id)], isSelectMultiple);
        });

        csRef.current?.on('boxselect', 'node', (e) => {
            handleBoxSelectionRef.current(Number(e.target._private.data.id));
        });

        csRef.current?.on('click', 'edge', (e) => {
            const isSelectMultiple = isSelectMultipleMouseEvent(e.originalEvent);

            const {source, target} = e.target._private.data;

            selectNodesRef.current([Number(source), Number(target)], isSelectMultiple);
        });
        // changesClearedCounter in hook dependencies to redraw graph when...
        // ...user discards presentation changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes, edges, setZoom, changesClearedOrSavedCounter]);
}
